import React, { useState } from 'react';
import { customersAPI } from '@/lib/api';
import { useQueryClient, useQuery } from '@tanstack/react-query';
import { toast } from 'sonner';
import { UserRound, Pencil, Check, X, Plus, Trash2, Search, ChevronUp, ChevronDown, ChevronsUpDown } from 'lucide-react';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from '@/components/ui/alert-dialog';

const EMPTY_NEW = { name: '', mobile: '', address: '', gst_number: '', fssai_number: '' };

const CustomerDetailsTab = () => {
  const qc = useQueryClient();
  const { data: customers = [], isLoading } = useQuery({
    queryKey: ['customers'],
    queryFn: () => customersAPI.getAll().then(r => r.data?.items ?? []),
    staleTime: 5 * 60 * 1000,
  });

  const [editMap, setEditMap]  = useState({});
  const [savingSet, setSaving] = useState(new Set());
  const [deletingSet, setDeleting] = useState(new Set());
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [adding, setAdding]    = useState(false);
  const [newRow, setNewRow]    = useState(EMPTY_NEW);
  const [addingSaving, setAddingSaving] = useState(false);
  const [search, setSearch]    = useState('');
  const [sortBy, setSortBy]    = useState('name');
  const [sortDir, setSortDir]  = useState('asc');

  const startEdit = (customer) => {
    setEditMap(prev => ({
      ...prev,
      [customer.id]: {
        mobile: customer.mobile || '',
        address: customer.address || '',
        gst_number: customer.gst_number || '',
        fssai_number: customer.fssai_number || '',
      },
    }));
  };

  const cancelEdit = (id) => {
    setEditMap(prev => { const n = { ...prev }; delete n[id]; return n; });
  };

  const handleSave = async (customer) => {
    const edits = editMap[customer.id];
    if (!edits) return;
    setSaving(s => new Set([...s, customer.id]));
    try {
      await customersAPI.update(customer.id, {
        mobile: edits.mobile,
        address: edits.address,
        gst_number: edits.gst_number,
        fssai_number: edits.fssai_number,
      });
      qc.setQueryData(['customers'], (prev = []) =>
        prev.map(c => c.id === customer.id ? { ...c, ...edits } : c)
      );
      cancelEdit(customer.id);
      toast.success(`${customer.name} saved`);
    } catch {
      toast.error('Could not save customer details');
    } finally {
      setSaving(s => { const n = new Set(s); n.delete(customer.id); return n; });
    }
  };

  const handleDelete = (customer) => {
    setDeleteTarget(customer);
  };

  const confirmDelete = async () => {
    const customer = deleteTarget;
    setDeleteTarget(null);
    setDeleting(s => new Set([...s, customer.id]));
    try {
      await customersAPI.delete(customer.id);
      qc.setQueryData(['customers'], (prev = []) => prev.filter(c => c.id !== customer.id));
      toast.success(`${customer.name} deleted`);
    } catch {
      toast.error('Could not delete customer');
    } finally {
      setDeleting(s => { const n = new Set(s); n.delete(customer.id); return n; });
    }
  };

  const handleAddSave = async () => {
    const name         = newRow.name.trim();
    const mobile       = newRow.mobile.trim();
    const address      = newRow.address.trim();
    const gst_number   = newRow.gst_number.trim();
    const fssai_number = newRow.fssai_number.trim();
    if (!name) { toast.error('Customer name is required'); return; }
    setAddingSaving(true);
    try {
      const r = await customersAPI.create({ name, mobile, address, gst_number, fssai_number });
      const created = r.data;
      qc.setQueryData(['customers'], (prev = []) =>
        [created, ...prev].sort((a, b) => a.name.localeCompare(b.name))
      );
      setAdding(false);
      setNewRow(EMPTY_NEW);
      toast.success(`${name} added`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Could not add customer');
    } finally {
      setAddingSaving(false);
    }
  };

  const handleAddCancel = () => { setAdding(false); setNewRow(EMPTY_NEW); };

  const q = search.toLowerCase();
  const visible = customers
    .filter(c =>
      !q ||
      c.name?.toLowerCase().includes(q) ||
      c.mobile?.includes(search) ||
      c.address?.toLowerCase().includes(q) ||
      String(c.account_code ?? '').includes(search)
    )
    .sort((a, b) => {
      let av = a[sortBy] ?? '';
      let bv = b[sortBy] ?? '';
      if (typeof av === 'number' && typeof bv === 'number') return sortDir === 'asc' ? av - bv : bv - av;
      av = String(av).toLowerCase();
      bv = String(bv).toLowerCase();
      return sortDir === 'asc' ? av.localeCompare(bv) : bv.localeCompare(av);
    });

  if (isLoading) {
    return (
      <div className="space-y-3">
        {[...Array(3)].map((_, i) => (
          <div key={i} className="h-12 bg-muted rounded-xl animate-pulse" />
        ))}
      </div>
    );
  }

  const khataBalance = deleteTarget ? Number(deleteTarget.khata_balance || 0) : 0;

  return (
    <div className="space-y-3">
      <AlertDialog open={!!deleteTarget} onOpenChange={open => !open && setDeleteTarget(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete &ldquo;{deleteTarget?.name}&rdquo;?</AlertDialogTitle>
            <AlertDialogDescription asChild>
              <div className="space-y-2 text-sm text-muted-foreground">
                {khataBalance > 0 && (
                  <p className="font-medium text-amber-700 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2">
                    ₹{khataBalance.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} is still outstanding in khata. Deleting this customer will permanently remove their outstanding balance from the system.
                  </p>
                )}
                <p>Their past sales history will not be affected.</p>
              </div>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={confirmDelete}
              className="bg-red-600 hover:bg-red-700 focus:ring-red-600 text-white"
            >
              Delete Customer
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <div className="flex items-center justify-between gap-3">
        <div className="relative flex-1 max-w-sm">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-muted-foreground pointer-events-none" />
          <input
            type="text"
            value={search}
            onChange={e => setSearch(e.target.value)}
            placeholder="Search customers…"
            className="w-full pl-8 pr-3 py-1.5 text-sm border border-border rounded-lg outline-none focus:border-primary"
          />
        </div>
        <p className="text-xs text-muted-foreground hidden lg:block">
          {customers.length > 0
            ? `${customers.length} customer${customers.length !== 1 ? 's' : ''}`
            : 'No customers yet'}
        </p>
        {!adding && (
          <button
            onClick={() => setAdding(true)}
            className="flex items-center gap-1.5 text-xs font-medium text-primary border border-primary/30 bg-primary/5 hover:bg-primary/10 px-3 py-1.5 rounded-lg transition-colors shrink-0"
          >
            <Plus className="h-3.5 w-3.5" />
            Add Customer
          </button>
        )}
      </div>

      <div className="border border-border rounded-xl overflow-hidden">
        <table className="w-full text-sm">
          <thead>
            <tr className="bg-muted/50 text-xs text-muted-foreground border-b border-border">
              {[{ col: 'account_code', label: 'Acct #', cls: 'w-16' }, { col: 'name', label: 'Customer Name', cls: '' }].map(({ col, label, cls }) => (
                <th
                  key={col}
                  className={`text-left px-4 py-3 font-medium cursor-pointer select-none hover:bg-slate-100 transition-colors whitespace-nowrap ${cls}`}
                  onClick={() => { setSortDir(d => sortBy === col ? (d === 'asc' ? 'desc' : 'asc') : 'asc'); setSortBy(col); }}
                >
                  {label}
                  {sortBy !== col ? <ChevronsUpDown className="w-3 h-3 opacity-30 inline ml-1" /> : sortDir === 'asc' ? <ChevronUp className="w-3 h-3 inline ml-1" /> : <ChevronDown className="w-3 h-3 inline ml-1" />}
                </th>
              ))}
              <th className="text-left px-4 py-3 font-medium w-44">Phone</th>
              <th className="text-left px-4 py-3 font-medium w-56">Address</th>
              <th className="text-left px-4 py-3 font-medium w-40">GST No.</th>
              <th className="text-left px-4 py-3 font-medium w-40">FSSAI No.</th>
              <th className="w-28 px-4 py-3" />
            </tr>
          </thead>
          <tbody className="divide-y divide-border">
            {adding && (
              <tr className="bg-primary/5">
                <td className="px-4 py-3 text-xs text-muted-foreground italic">auto</td>
                <td className="px-4 py-3">
                  <input
                    autoFocus
                    type="text"
                    value={newRow.name}
                    onChange={e => setNewRow(p => ({ ...p, name: e.target.value }))}
                    placeholder="Customer name"
                    className="w-full text-sm border border-border rounded-lg px-2.5 py-1 outline-none focus:border-primary"
                  />
                </td>
                <td className="px-4 py-3">
                  <input
                    type="tel"
                    value={newRow.mobile}
                    onChange={e => setNewRow(p => ({ ...p, mobile: e.target.value }))}
                    placeholder="+91 98765 43210"
                    className="w-full text-sm border border-border rounded-lg px-2.5 py-1 outline-none focus:border-primary"
                  />
                </td>
                <td className="px-4 py-3">
                  <input
                    type="text"
                    value={newRow.address}
                    onChange={e => setNewRow(p => ({ ...p, address: e.target.value }))}
                    placeholder="Address (optional)"
                    className="w-full text-sm border border-border rounded-lg px-2.5 py-1 outline-none focus:border-primary"
                  />
                </td>
                <td className="px-4 py-3">
                  <input
                    type="text"
                    value={newRow.gst_number}
                    onChange={e => setNewRow(p => ({ ...p, gst_number: e.target.value }))}
                    placeholder="GST No. (optional)"
                    className="w-full text-sm border border-border rounded-lg px-2.5 py-1 outline-none focus:border-primary"
                  />
                </td>
                <td className="px-4 py-3">
                  <input
                    type="text"
                    value={newRow.fssai_number}
                    onChange={e => setNewRow(p => ({ ...p, fssai_number: e.target.value }))}
                    placeholder="FSSAI No. (optional)"
                    onKeyDown={e => { if (e.key === 'Enter') handleAddSave(); if (e.key === 'Escape') handleAddCancel(); }}
                    className="w-full text-sm border border-border rounded-lg px-2.5 py-1 outline-none focus:border-primary"
                  />
                </td>
                <td className="px-4 py-3">
                  <div className="flex items-center gap-1.5 justify-end">
                    <button
                      onClick={handleAddSave}
                      disabled={addingSaving}
                      className="flex items-center gap-1 text-xs text-emerald-700 bg-emerald-50 hover:bg-emerald-100 border border-emerald-200 px-2.5 py-1 rounded-lg transition-colors disabled:opacity-50"
                    >
                      <Check className="h-3 w-3" />
                      {addingSaving ? 'Saving…' : 'Save'}
                    </button>
                    <button
                      onClick={handleAddCancel}
                      className="p-1 text-muted-foreground hover:text-foreground rounded transition-colors"
                    >
                      <X className="h-3.5 w-3.5" />
                    </button>
                  </div>
                </td>
              </tr>
            )}

            {visible.length === 0 && !adding && (
              <tr>
                <td colSpan={7} className="px-4 py-10 text-center text-muted-foreground">
                  <UserRound className="h-8 w-8 mx-auto mb-2 opacity-25" />
                  <p className="text-sm">{search ? 'No customers match your search' : 'No customers yet'}</p>
                  {!search && <p className="text-xs mt-1">Add one manually or complete a POS sale to auto-populate.</p>}
                </td>
              </tr>
            )}

            {visible.map(customer => {
              const editing  = customer.id in editMap;
              const saving   = savingSet.has(customer.id);
              const deleting = deletingSet.has(customer.id);
              const edits    = editMap[customer.id] || {};

              return (
                <tr key={customer.id} className="hover:bg-muted/20 transition-colors">
                  <td className="px-4 py-3 text-xs font-mono text-muted-foreground">
                    {customer.account_code ?? '—'}
                  </td>

                  <td className="px-4 py-3 font-medium text-foreground">{customer.name}</td>

                  <td className="px-4 py-3">
                    {editing ? (
                      <input
                        type="tel"
                        value={edits.mobile}
                        onChange={e => setEditMap(prev => ({
                          ...prev,
                          [customer.id]: { ...prev[customer.id], mobile: e.target.value },
                        }))}
                        placeholder="+91 98765 43210"
                        className="w-full text-sm border border-border rounded-lg px-2.5 py-1 outline-none focus:border-primary"
                      />
                    ) : (
                      <span className={customer.mobile ? 'text-foreground' : 'text-muted-foreground italic text-xs'}>
                        {customer.mobile || '—'}
                      </span>
                    )}
                  </td>

                  <td className="px-4 py-3">
                    {editing ? (
                      <input
                        type="text"
                        value={edits.address}
                        onChange={e => setEditMap(prev => ({
                          ...prev,
                          [customer.id]: { ...prev[customer.id], address: e.target.value },
                        }))}
                        placeholder="Address"
                        className="w-full text-sm border border-border rounded-lg px-2.5 py-1 outline-none focus:border-primary"
                      />
                    ) : (
                      <span className={customer.address ? 'text-foreground' : 'text-muted-foreground italic text-xs'}>
                        {customer.address || '—'}
                      </span>
                    )}
                  </td>

                  <td className="px-4 py-3">
                    {editing ? (
                      <input
                        type="text"
                        value={edits.gst_number}
                        onChange={e => setEditMap(prev => ({
                          ...prev,
                          [customer.id]: { ...prev[customer.id], gst_number: e.target.value },
                        }))}
                        placeholder="GST No."
                        className="w-full text-sm border border-border rounded-lg px-2.5 py-1 outline-none focus:border-primary"
                      />
                    ) : (
                      <span className={customer.gst_number ? 'text-foreground font-mono text-xs' : 'text-muted-foreground italic text-xs'}>
                        {customer.gst_number || '—'}
                      </span>
                    )}
                  </td>

                  <td className="px-4 py-3">
                    {editing ? (
                      <input
                        type="text"
                        value={edits.fssai_number}
                        onChange={e => setEditMap(prev => ({
                          ...prev,
                          [customer.id]: { ...prev[customer.id], fssai_number: e.target.value },
                        }))}
                        placeholder="FSSAI No."
                        className="w-full text-sm border border-border rounded-lg px-2.5 py-1 outline-none focus:border-primary"
                      />
                    ) : (
                      <span className={customer.fssai_number ? 'text-foreground font-mono text-xs' : 'text-muted-foreground italic text-xs'}>
                        {customer.fssai_number || '—'}
                      </span>
                    )}
                  </td>

                  <td className="px-4 py-3">
                    {editing ? (
                      <div className="flex items-center gap-1.5 justify-end">
                        <button
                          onClick={() => handleSave(customer)}
                          disabled={saving}
                          className="flex items-center gap-1 text-xs text-emerald-700 bg-emerald-50 hover:bg-emerald-100 border border-emerald-200 px-2.5 py-1 rounded-lg transition-colors disabled:opacity-50"
                        >
                          <Check className="h-3 w-3" />
                          {saving ? 'Saving…' : 'Save'}
                        </button>
                        <button
                          onClick={() => cancelEdit(customer.id)}
                          className="p-1 text-muted-foreground hover:text-foreground rounded transition-colors"
                        >
                          <X className="h-3.5 w-3.5" />
                        </button>
                      </div>
                    ) : (
                      <div className="flex items-center gap-1.5 justify-end">
                        <button
                          onClick={() => startEdit(customer)}
                          className="flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground border border-border px-2.5 py-1 rounded-lg transition-colors"
                        >
                          <Pencil className="h-3 w-3" />
                          Edit
                        </button>
                        <button
                          onClick={() => handleDelete(customer)}
                          disabled={deleting}
                          className="p-1 text-muted-foreground hover:text-red-600 rounded transition-colors disabled:opacity-40"
                          title="Delete customer"
                        >
                          <Trash2 className="h-3.5 w-3.5" />
                        </button>
                      </div>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default CustomerDetailsTab;
